/** Centralised appointment status and category styling. */

import {
  Check,
  ClipboardList,
  Clock,
  CloudMoon,
  HeartPulse,
  LogIn,
  type LucideIcon,
  Microscope,
  Scissors,
  ShowerHead,
  Stethoscope,
  Syringe,
  UserX,
  Video,
  X,
} from 'lucide-react'

import type {
  AppointmentStatus,
  AppointmentTypeCategoryId,
  AppointmentWithRelations,
} from '@snootsvet/shared'
import { APPOINTMENT_TYPES_BY_ID } from '@snootsvet/shared'

import type { BadgeVariant } from '@/frontend/components/ui/badge'

export interface AppointmentStatusStyle {
  label: string
  icon: LucideIcon

  color: BadgeVariant
  /** When set, overrides category colours on calendar events (for terminal statuses). */
  eventColors?: { bg: string; border: string; text: string }
}

/** UI config for each appointment display status. */
export const APPOINTMENT_STATUS_STYLES: Record<AppointmentStatus, AppointmentStatusStyle> = {
  pending: { label: 'Pending', icon: Clock, color: 'pending' },
  confirmed: { label: 'Confirmed', icon: Check, color: 'success' },
  'checked-in': { label: 'Checked in', icon: LogIn, color: 'info' },
  'in-progress': { label: 'In progress', icon: Stethoscope, color: 'success' },
  completed: { label: 'Completed', icon: Check, color: 'info' },
  cancelled: {
    label: 'Cancelled',
    icon: X,
    color: 'neutral',
    eventColors: {
      bg: 'var(--neutral)',
      border: 'var(--neutral-border)',
      text: 'var(--neutral-fg)',
    },
  },
  'no-show': {
    label: 'No-show',
    icon: UserX,
    color: 'destructive',
    eventColors: {
      bg: 'var(--neutral)',
      border: 'var(--neutral-border)',
      text: 'var(--neutral-fg)',
    },
  },
}

const DEFAULT_STATUS_STYLE: AppointmentStatusStyle = {
  label: 'Unknown',
  icon: Clock,
  color: 'neutral',
}

/** Returns the UI style config for a given appointment display status. */
export function getStatusStyle(status: string): AppointmentStatusStyle {
  if (Object.hasOwn(APPOINTMENT_STATUS_STYLES, status)) {
    return APPOINTMENT_STATUS_STYLES[status as AppointmentStatus]
  }
  return DEFAULT_STATUS_STYLE
}

/**
 * Derives the display status for an appointment based on its consultation state.
 * Only promotes when the appointment is checked-in and the consultation has
 * progressed through the expected lifecycle — otherwise falls back to the
 * stored appointment status.
 */
export function getDisplayStatus(appointment: AppointmentWithRelations): string {
  if (appointment.status === 'checked-in' && appointment.consultation?.finalisedAt)
    return 'completed'
  if (appointment.status === 'checked-in' && appointment.consultation) return 'in-progress'
  return appointment.status
}

/** Category styling for appointment type colouring and icons (calendar events, cards). */
export const CATEGORY_STYLES: Record<
  AppointmentTypeCategoryId,
  { bg: string; border: string; text: string; icon: LucideIcon }
> = {
  wellness: { bg: '#e0f2fe', border: '#bae6fd', text: '#0c4a6e', icon: HeartPulse },
  vaccs: { bg: '#ede9fe', border: '#ddd6fe', text: '#3b0764', icon: Syringe },
  surgery: { bg: '#f5f9d8', border: '#e4f5a0', text: '#1a2e05', icon: Scissors },
  grooming: { bg: '#d9f9f1', border: '#99f6e4', text: '#0d3d38', icon: ShowerHead },
  admin: { bg: '#f1f5f9', border: '#e2e8f0', text: '#1e293b', icon: ClipboardList },
  eol: { bg: '#f5f5f4', border: '#e7e5e4', text: '#1c1917', icon: CloudMoon },
  diagnostics: { bg: '#fef3dc', border: '#fcc96e', text: '#3b1a04', icon: Microscope },
  telemedicine: { bg: '#ffe4e6', border: '#fecdd3', text: '#4c0519', icon: Video },
}

/** Returns the category style config for a given appointment type ID. */
export function getCategoryStyle(appointmentTypeId: string) {
  const category = APPOINTMENT_TYPES_BY_ID[appointmentTypeId]?.category ?? 'wellness'
  return CATEGORY_STYLES[category]
}
